import * as React from "react";
import { Form } from "react-bootstrap";
import { TempRequest } from "../types";

export interface FilterState {
  statuses: Set<string>;
  practiceSearch: string;
  positionType: string;
  dateFrom: string;
  dateTo: string;
}

interface TempRequestsFilterBarProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  tempRequests: TempRequest[];
}

const TempRequestsFilterBar: React.FC<TempRequestsFilterBarProps> = ({
  filters,
  onFiltersChange,
  tempRequests
}) => {
  // Get unique position types from temp requests
  const positionTypes = React.useMemo(() => {
    const types = new Set(tempRequests.map(tr => tr.job.job_type.title));
    return Array.from(types).sort();
  }, [tempRequests]);

  // Get unique practices for autocomplete
  const practices = React.useMemo(() => {
    const practiceSet = new Map<number, string>();
    tempRequests.forEach(tr => {
      if (!practiceSet.has(tr.job.practice.id)) {
        practiceSet.set(tr.job.practice.id, tr.job.practice.name);
      }
    });
    return Array.from(practiceSet.values()).sort();
  }, [tempRequests]);

  // Filter practices based on search input
  const practiceSuggestions = React.useMemo(() => {
    if (!filters.practiceSearch) return [];
    return practices.filter(p =>
      p.toLowerCase().includes(filters.practiceSearch.toLowerCase())
    );
  }, [practices, filters.practiceSearch]);

  const [showPracticeDropdown, setShowPracticeDropdown] = React.useState(false);

  const handleStatusToggle = (status: string) => {
    const newStatuses = new Set(filters.statuses);
    if (newStatuses.has(status)) {
      newStatuses.delete(status);
    } else {
      newStatuses.add(status);
    }
    onFiltersChange({ ...filters, statuses: newStatuses });
  };

  const handleSelectAllStatuses = () => {
    onFiltersChange({
      ...filters,
      statuses: new Set(['pending', 'accepted', 'declined', 'cancelled'])
    });
  };

  const handleClearAllStatuses = () => {
    onFiltersChange({
      ...filters,
      statuses: new Set()
    });
  };

  const handlePracticeSearch = (value: string) => {
    onFiltersChange({ ...filters, practiceSearch: value });
    if (value) {
      setShowPracticeDropdown(true);
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      statuses: new Set(),
      practiceSearch: "",
      positionType: "",
      dateFrom: "",
      dateTo: ""
    });
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.statuses.size > 0 ||
    filters.practiceSearch ||
    filters.positionType ||
    filters.dateFrom ||
    filters.dateTo;

  // Count active filters
  const activeFilterCount =
    filters.statuses.size +
    (filters.practiceSearch ? 1 : 0) +
    (filters.positionType ? 1 : 0) +
    (filters.dateFrom || filters.dateTo ? 1 : 0);

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e8ecef',
        borderRadius: '0.75rem',
        padding: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
      }}
    >
      {/* Status Checkboxes */}
      <div className="mb-3">
        <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem', display: 'block' }}>
          <i className="fa fa-filter me-2"></i>Status
        </label>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div className="d-flex gap-2">
            {/* Pending - Yellow/Orange */}
            <label
              htmlFor="status-pending"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: filters.statuses.has('pending') ? 'linear-gradient(135deg, #ffc107, #ffb300)' : '#fff',
                color: filters.statuses.has('pending') ? 'white' : '#333',
                padding: '0.375rem 0.875rem',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: `2px solid ${filters.statuses.has('pending') ? '#ffc107' : '#e8ecef'}`,
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="checkbox"
                id="status-pending"
                checked={filters.statuses.has('pending')}
                onChange={() => handleStatusToggle('pending')}
                style={{ cursor: 'pointer' }}
              />
              Pending
            </label>

            {/* Accepted - Green */}
            <label
              htmlFor="status-accepted"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: filters.statuses.has('accepted') ? 'linear-gradient(135deg, #71ad08, #5a8a06)' : '#fff',
                color: filters.statuses.has('accepted') ? 'white' : '#333',
                padding: '0.375rem 0.875rem',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: `2px solid ${filters.statuses.has('accepted') ? '#71ad08' : '#e8ecef'}`,
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="checkbox"
                id="status-accepted"
                checked={filters.statuses.has('accepted')}
                onChange={() => handleStatusToggle('accepted')}
                style={{ cursor: 'pointer' }}
              />
              Accepted
            </label>

            {/* Declined - Red */}
            <label
              htmlFor="status-declined"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: filters.statuses.has('declined') ? 'linear-gradient(135deg, #dc3545, #b02a37)' : '#fff',
                color: filters.statuses.has('declined') ? 'white' : '#333',
                padding: '0.375rem 0.875rem',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: `2px solid ${filters.statuses.has('declined') ? '#dc3545' : '#e8ecef'}`,
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="checkbox"
                id="status-declined"
                checked={filters.statuses.has('declined')}
                onChange={() => handleStatusToggle('declined')}
                style={{ cursor: 'pointer' }}
              />
              Declined
            </label>

            {/* Cancelled - Red */}
            <label
              htmlFor="status-cancelled"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: filters.statuses.has('cancelled') ? 'linear-gradient(135deg, #dc3545, #b02a37)' : '#fff',
                color: filters.statuses.has('cancelled') ? 'white' : '#333',
                padding: '0.375rem 0.875rem',
                borderRadius: '50px',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: `2px solid ${filters.statuses.has('cancelled') ? '#dc3545' : '#e8ecef'}`,
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="checkbox"
                id="status-cancelled"
                checked={filters.statuses.has('cancelled')}
                onChange={() => handleStatusToggle('cancelled')}
                style={{ cursor: 'pointer' }}
              />
              Cancelled
            </label>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <button
              type="button"
              onClick={handleSelectAllStatuses}
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleClearAllStatuses}
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter Inputs */}
      <div className="d-flex gap-3 flex-wrap align-items-end">
        {/* Practice Search */}
        <div style={{ width: "250px", position: "relative" }}>
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
              Practice
            </Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search by practice name"
                value={filters.practiceSearch}
                onChange={(e) => handlePracticeSearch(e.target.value)}
                onBlur={() => setTimeout(() => setShowPracticeDropdown(false), 200)}
                onFocus={() => filters.practiceSearch && setShowPracticeDropdown(true)}
                style={{
                  borderRadius: '0.5rem',
                  border: '1px solid #e8ecef',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.9rem'
                }}
              />
              {filters.practiceSearch && (
                <div
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    zIndex: 5,
                    color: '#6c757d'
                  }}
                  onClick={() => handlePracticeSearch("")}
                >
                  <i className="fa fa-times"></i>
                </div>
              )}
            </div>
            {practiceSuggestions.length > 0 && showPracticeDropdown && (
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  zIndex: 1000,
                  backgroundColor: "#fafafa",
                  border: "1px solid #dee2e6",
                  borderRadius: "0.25rem",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  marginTop: "2px",
                  maxHeight: "200px",
                  overflowY: "auto"
                }}
              >
                {practiceSuggestions.map((practice, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      handlePracticeSearch(practice);
                      setShowPracticeDropdown(false);
                    }}
                    className="px-3 py-2"
                    style={{
                      cursor: "pointer",
                      transition: "background 0.15s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#176fc9"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                  >
                    {practice}
                  </div>
                ))}
              </div>
            )}
          </Form.Group>
        </div>

        {/* Position Type */}
        <div style={{ width: "200px" }}>
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
              Position Type
            </Form.Label>
            <Form.Select
              value={filters.positionType}
              onChange={(e) => onFiltersChange({ ...filters, positionType: e.target.value })}
              style={{
                borderRadius: '0.5rem',
                border: '1px solid #e8ecef',
                padding: '0.5rem 0.75rem',
                fontSize: '0.9rem'
              }}
            >
              <option value="">All Positions</option>
              {positionTypes.map((type, index) => (
                <option key={index} value={type}>{type}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>

        {/* Date From */}
        <div style={{ width: "160px" }}>
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
              From Date
            </Form.Label>
            <Form.Control
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onFiltersChange({ ...filters, dateFrom: e.target.value })}
              style={{
                borderRadius: '0.5rem',
                border: '1px solid #e8ecef',
                padding: '0.5rem 0.75rem',
                fontSize: '0.9rem'
              }}
            />
          </Form.Group>
        </div>

        {/* Date To */}
        <div style={{ width: "160px" }}>
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.25rem' }}>
              To Date
            </Form.Label>
            <Form.Control
              type="date"
              value={filters.dateTo}
              onChange={(e) => onFiltersChange({ ...filters, dateTo: e.target.value })}
              style={{
                borderRadius: '0.5rem',
                border: '1px solid #e8ecef',
                padding: '0.5rem 0.75rem',
                fontSize: '0.9rem'
              }}
            />
          </Form.Group>
        </div>

        {/* Clear All Filters */}
        <button
          className="btn-modern btn-modern--ghost btn-modern--sm"
          onClick={clearAllFilters}
          style={{
            opacity: hasActiveFilters ? 1 : 0.6,
            whiteSpace: 'nowrap'
          }}
          disabled={!hasActiveFilters}
        >
          <i className="fa fa-times"></i> Clear all filters
          {activeFilterCount > 0 && (
            <span className="badge bg-primary ms-2">{activeFilterCount}</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default TempRequestsFilterBar;
